import React, { FunctionComponent, useMemo } from "react";
import {
  FormControl,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  SelectChangeEvent,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import "../../theme-vars.css";

type Option = { label: string; value: string };

type Props = {
  title?: string;
  option?: string[];
  choices?: Option[];
  setDetails?: () => void;
  selectedValue?: Option | Option[];
  setSelectedValue: (val: any) => void;
  disable?: boolean;
  isMulti?: boolean;
  style?: React.CSSProperties;
  required?: boolean;
  maxWidth?: number;
};

const primaryColor = "#292744";

const CustomFormControl = styled(FormControl)({
  minWidth: "100%",
  "& .MuiInputLabel-root": {
    color: primaryColor,
    fontWeight: 500,
    fontSize: "var(--font-size-medium)",
    fontFamily: "var(--font-family-main)",
    background: "#f7f8fa",
    padding: "0 4px",
  },
  "& .MuiInputLabel-root.Mui-required .MuiInputLabel-asterisk": {
    color: "#d32f2f",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },
  "& .MuiSelect-select": {
    padding: "14px",
    fontSize: "var(--font-size-medium)",
    color: "#222",
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontFamily: "var(--font-family-main)",
  },
  "& .MuiMenu-paper": {
    fontFamily: "var(--font-family-main)",
    fontSize: "var(--font-size-medium)",
  },
  "& .MuiMenuItem-root": {
    fontFamily: "var(--font-family-main)",
    fontSize: "var(--font-size-medium)",
  },
  "& .MuiListItemText-root": {
    fontFamily: "var(--font-family-main)",
    fontSize: "var(--font-size-medium)",
  },
});

const Dropdown: FunctionComponent<Props> = ({
  title,
  option,
  choices,
  setDetails,
  selectedValue,
  setSelectedValue,
  disable,
  isMulti,
  style,
  required,
  maxWidth = 220
}) => {
  
  const buildOptions = (): Option[] => {
    let data: Option[] = [];
    if (option) {
      data = option.map((item: string) =>
        item === "Team Lead"
          ? { value: item, label: "Team Leader" }
          : { value: item, label: item }
      );
    } else {
      data = choices || [];
    }
    return data;
  };

  const opts = buildOptions();

  const value = isMulti
    ? Array.isArray(selectedValue)
      ? (selectedValue as Option[]).map((o) => o.value)
      : []
    : (selectedValue as Option)?.value || "";

  const handleChange = (event: SelectChangeEvent<any>) => {
    if (isMulti) {
      const selectedVals = event.target.value as string[];
      const selectedOptions = opts.filter((o) =>
        selectedVals.includes(o.value)
      );
      setSelectedValue(selectedOptions);
    } else {
      const selectedVal = event.target.value;
      const selectedOption = opts.find((o) => o.value === selectedVal);
      if (selectedOption?.value === title && setDetails) {
        setDetails();
      }
      setSelectedValue(selectedOption);
    }
  };

  return (
    <CustomFormControl
      variant="outlined"
      style={style}
      disabled={disable || opts.length === 0}
      required={required}
    >
      {title && (
        <InputLabel
          style={{
            color: "#555",
            fontFamily: "var(--font-family-main)",
            fontSize: "var(--font-size-medium)",
          }}
          required={required}
        >
          {title}
        </InputLabel>
      )}
      <Select
        label={title}
        multiple={isMulti}
        value={value}
        onChange={handleChange}
        sx={{
          ".MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: maxWidth,
          },
        }}
        renderValue={(selected) =>
          isMulti
            ? (selected as string[])
              .map((val) => opts.find((o) => o.value === val)?.label || val)
              .join(", ")
            : opts.find((o) => o.value === selected)?.label || ""
        }
      >
        {opts.length === 0 ? (
          <MenuItem disabled>No options available</MenuItem>
        ) : (
          opts.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {isMulti && (
                <Checkbox
                  checked={((value as string[]) || []).indexOf(opt.value) > -1}
                  sx={{
                    color: "#292744",
                    "&.Mui-checked": { color: "#292744" },
                    marginRight: "8px",
                    padding: 0,
                  }}
                />
              )}
              <ListItemText
                primaryTypographyProps={{
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-medium)",
                  maxWidth: maxWidth,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  margin: 0,
                }}
                sx={{ margin: 0 }}
                primary={opt.label}
              />
            </MenuItem>
          ))
        )}
      </Select>
    </CustomFormControl>
  );
};

export default Dropdown;
